import React, { useRef, useState } from "react";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Upload, FileText, Check, Download, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function OutlineUploader({ programId, onUploaded, initialPreview, initialFilename }) {
    const [uploading, setUploading] = useState(false);
    const [preview, setPreview] = useState(initialPreview || "");
    const [filename, setFilename] = useState(initialFilename || "");
    const [charCount, setCharCount] = useState(0);
    const [truncated, setTruncated] = useState(false);
    const inputRef = useRef(null);

    const handleFiles = async (files) => {
        const file = files?.[0];
        if (!file) return;
        const name = file.name.toLowerCase();
        if (!name.endsWith(".pdf") && !name.endsWith(".docx")) {
            toast.error("Please upload a PDF or .docx file");
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post(
                `/programs/${programId}/upload-outline`,
                fd,
                { headers: { "Content-Type": "multipart/form-data" } },
            );
            setPreview(data.preview);
            setFilename(data.filename);
            setCharCount(data.char_count);
            setTruncated(data.truncated);
            toast.success("Outline uploaded");
            onUploaded?.(data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Upload failed");
        } finally {
            setUploading(false);
        }
    };

    const downloadTemplate = () => {
        window.open(`${API}/outline-template`, "_blank");
    };

    return (
        <div className="space-y-4" data-testid="outline-uploader">
            <div className="flex items-center justify-between">
                <div>
                    <div className="font-display text-lg text-primary">Upload program outline</div>
                    <p className="text-sm text-muted-foreground">
                        AI will use this as the foundation for generating detailed chapters and
                        lessons.
                    </p>
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={downloadTemplate}
                    data-testid="download-outline-template-btn"
                >
                    <Download className="h-4 w-4 mr-2" />
                    Download template
                </Button>
            </div>

            <label
                htmlFor="outline-file-input"
                className="block border-2 border-dashed border-border rounded-sm p-8 text-center cursor-pointer hover:border-primary/50 hover:bg-secondary/30 transition-colors"
                onDragOver={(e) => {
                    e.preventDefault();
                }}
                onDrop={(e) => {
                    e.preventDefault();
                    handleFiles(e.dataTransfer.files);
                }}
                data-testid="outline-dropzone"
            >
                <input
                    id="outline-file-input"
                    ref={inputRef}
                    type="file"
                    accept=".pdf,.docx"
                    className="hidden"
                    onChange={(e) => handleFiles(e.target.files)}
                    data-testid="outline-file-input"
                />
                <Upload className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                <div className="text-sm font-medium text-foreground">
                    {uploading ? "Uploading…" : "Click to upload or drag & drop"}
                </div>
                <div className="text-xs text-muted-foreground mt-1">PDF or Word (.docx), up to ~40k characters</div>
            </label>

            {preview && (
                <Card className="border-border" data-testid="outline-preview-card">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-2 mb-2">
                            <FileText className="h-4 w-4 text-primary" />
                            <div className="font-medium text-foreground">{filename}</div>
                            <span className="ml-auto text-xs text-muted-foreground inline-flex items-center gap-1">
                                <Check className="h-3 w-3 text-accent" />
                                {charCount ? `${charCount.toLocaleString()} chars extracted` : "Loaded"}
                            </span>
                        </div>
                        {truncated && (
                            <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-sm p-2 mb-2">
                                <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                                <span>
                                    Outline was truncated to 40,000 characters. Consider shortening
                                    or splitting the source.
                                </span>
                            </div>
                        )}
                        <div className="text-xs text-muted-foreground whitespace-pre-wrap bg-secondary/30 rounded-sm p-3 max-h-40 overflow-y-auto">
                            {preview}
                            {charCount > preview.length && "…"}
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
